import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, Calculator, User } from 'lucide-react';

const InfoPanel = ({ appointment, team, assignedInstaller, userNames, onNavigate, onCalculateCosts, startAddress, isFirstOfDay }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500">{t('appointment.team')}</p>
          <p className="font-medium">{team?.name || "-"}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('appointment.date')}/{t('appointment.time')}</p>
          <p className="font-medium">
            {new Date(appointment.scheduled_date).toLocaleDateString("it-IT")} - {appointment.scheduled_time}
            {appointment.scheduled_end_time && (
              <span className="text-gray-500"> / {appointment.scheduled_end_time}</span>
            )}
          </p>
        </div>
      </div>

      {/* Assigned Installer */}
      {(assignedInstaller || appointment.assigned_installer_id) && (
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">{t('appointment.assignedInstaller')}</p>
          <p className="font-medium">
            {assignedInstaller ? `${assignedInstaller.first_name} ${assignedInstaller.last_name}` : '-'}
          </p>
          {assignedInstaller?.starting_address && (
            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
              <MapPin size={12} /> {assignedInstaller.starting_address}
            </p>
          )}
        </div>
      )}

      <div>
        <p className="text-sm text-gray-500">{t('appointment.clientName')}</p>
        <p className="font-medium">{appointment.client_name}</p>
        <p className="text-sm text-gray-600">{appointment.client_tax_id}</p>
      </div>

      <div>
        <p className="text-sm text-gray-500">{t('appointment.address')}</p>
        {appointment.site_name && (
          <p className="font-semibold text-gray-800 text-sm" data-testid="appointment-site-name">
            {appointment.site_name}
          </p>
        )}
        <p className="font-medium">{appointment.address}</p>
        <p className="text-sm text-gray-600">{appointment.city} ({appointment.province})</p>

        {appointment.manual_address && (
          <p className="mt-1 text-xs text-blue-700 font-medium" data-testid="manual-address-note">
            🏠 Indirizzo manuale (domicilio cliente) — protetto dal Sync Salesforce
          </p>
        )}

        {appointment.sf_site_updated_at && (
          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200" data-testid="sf-site-updated-note">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
              <MapPin size={12} /> Indirizzo aggiornato da Salesforce il{' '}
              {new Date(appointment.sf_site_updated_at).toLocaleDateString('it-IT')}
            </p>
            {appointment.sf_site_update_note && (
              <p className="text-xs text-amber-700 mt-0.5">{appointment.sf_site_update_note}</p>
            )}
          </div>
        )}

        {/* Referente */}
        {(appointment.contact_name || appointment.contact_phone) && (
          <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
            <p className="text-xs text-gray-500 uppercase">Referente</p>
            <p className="text-sm font-medium text-gray-800">{appointment.contact_name || '-'}</p>
            {appointment.contact_phone && (
              <a href={`tel:${appointment.contact_phone}`} className="text-sm text-blue-600 hover:underline">{appointment.contact_phone}</a>
            )}
          </div>
        )}

        {/* Starting Point Info */}
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">
              {t('appointment.startingPoint') || 'Punto di partenza'}
            </p>
            <p className="text-sm font-medium text-gray-800 mt-1">
              {startAddress || (t('appointment.notSet') || 'Non impostato')}
            </p>
            {!isFirstOfDay && !appointment.custom_start_address && (
              <p className="text-xs text-blue-600 mt-1">
                {t('appointment.fromPreviousAppointment') || 'Da appuntamento precedente'}
              </p>
            )}
            {isFirstOfDay && !appointment.custom_start_address && (
              <p className="text-xs text-green-600 mt-1">
                {t('appointment.fromInstallerHome') || 'Dalla sede installatore'}
              </p>
            )}
            {appointment.custom_start_address && (
              <p className="text-xs text-orange-600 mt-1">
                {t('appointment.customAddress') || 'Indirizzo personalizzato'}
              </p>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={onNavigate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
            data-testid="navigate-btn">
            <Navigation size={16} />
            {t('appointment.navigate')}
          </button>
          <button onClick={onCalculateCosts}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
            data-testid="calculate-costs-btn">
            <Calculator size={16} />
            {t('appointment.calculateCosts')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500">{t('appointment.vehicleBrand')}</p>
          <p className="font-medium">{appointment.vehicle_brand} {appointment.vehicle_model}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('appointment.licensePlate')}</p>
          <p className="font-medium">{appointment.license_plate}</p>
        </div>
      </div>

      {appointment.installation_type && (
        <div>
          <p className="text-sm text-gray-500">{t('appointment.installationType')}</p>
          <p className="font-medium capitalize">{t(`appointment.installationTypes.${appointment.installation_type}`) || appointment.installation_type}</p>
        </div>
      )}

      {/* Device delivery info - from installations or top-level */}
      {(() => {
        const deliveryLabels = { in_loco: 'Dispositivo in loco', anticipato_tecnico: 'Da anticipare dal tecnico' };
        const installs = appointment.installations;
        if (installs && installs.length > 0) {
          const withDelivery = installs.filter(i => i.device_delivery);
          if (withDelivery.length > 0) {
            return (
              <div>
                <p className="text-sm text-gray-500">Consegna Dispositivo</p>
                {withDelivery.map((inst, idx) => (
                  <p key={idx} className="font-medium">
                    {inst.device_type && <span className="text-gray-500 text-xs">{inst.device_type}: </span>}
                    {deliveryLabels[inst.device_delivery] || inst.device_delivery}
                  </p>
                ))}
              </div>
            );
          }
        } else if (appointment.device_delivery) {
          return (
            <div>
              <p className="text-sm text-gray-500">Consegna Dispositivo</p>
              <p className="font-medium">{deliveryLabels[appointment.device_delivery] || appointment.device_delivery}</p>
            </div>
          );
        }
        return null;
      })()}

      {appointment.notes && (
        <div>
          <p className="text-sm text-gray-500">{t('appointment.notes')}</p>
          <p className="font-medium">{appointment.notes}</p>
        </div>
      )}

      {appointment.status_reason && (
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">{t('appointment.statusReason')}</p>
          <p className="font-medium text-gray-700">{appointment.status_reason}</p>
        </div>
      )}

      {/* Created By Info */}
      {appointment.created_by && (
        <div className="flex items-center justify-between text-sm border-t pt-3">
          <div className="flex items-center gap-2 text-gray-500">
            <User size={14} />
            <span>{t('appointment.createdBy')}:</span>
            <span className="font-medium text-gray-700">
              {userNames[appointment.created_by] || 'Operatore'}
            </span>
          </div>
          {appointment.created_at && (
            <span className="text-xs text-gray-400">
              {new Date(appointment.created_at).toLocaleDateString('it-IT')} {new Date(appointment.created_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
            </span>
          )}
        </div>
      )}
    </>
  );
};

export default InfoPanel;
